import CryptoJS from 'crypto-js';

export const CONTEXT_TYPE_MY_INSIGHT = 'MY_INSIGHT';
export const CONTEXT_TYPE_MANAGER = 'MANAGER';

export const ACCOUNT_CONTEXTS_STORAGE_KEY = 'insightAccountContexts';
export const ACTIVE_CONTEXT_ID_STORAGE_KEY = 'insightActiveContextId';
export const POST_LOGIN_PENDING_STORAGE_KEY = 'insightPostLoginPending';
export const LOGIN_SNAPSHOT_STORAGE_KEY = 'insightLoginSnapshot';

const MANAGER_ROLE = '2';
const ADMIN_ROLE = '1';
const GENERIC_ROLE = '4';
const SECONDARY_ADMIN_ROLE = '7';

const cookieSecure = () => Boolean(process.env.secureCoockie);

function isSupportAgentSession() {
  try {
    return ['s1', 's2', 's3'].includes(String(localStorage.getItem('accessLevel') || ''));
  } catch {
    return false;
  }
}

function isSupportImpersonationSession() {
  try {
    return document.cookie.split(';').some((part) => part.trim().startsWith('userSubRole='));
  } catch {
    return false;
  }
}

function contextId(context) {
  if (!context) return '';
  if (context.contextType === CONTEXT_TYPE_MY_INSIGHT) {
    return context.accountId ? `${CONTEXT_TYPE_MY_INSIGHT}:${context.accountId}` : CONTEXT_TYPE_MY_INSIGHT;
  }
  return `${context.contextType}:${context.accountId || ''}`;
}

function normalizeUserRoles(userRoleField) {
  if (!userRoleField) return [];
  if (Array.isArray(userRoleField)) return userRoleField;
  if (typeof userRoleField === 'object') return [userRoleField];
  return [];
}

function roleParentId(roleEntry) {
  return roleEntry?.parentId || roleEntry?.parent_id || roleEntry?.parentID;
}

/** Login-level accessType (1=RO, 2=RW). Used when a manager context omitted accessType. */
function loginAccessType(loginPayload) {
  const value =
    loginPayload?.accessType ??
    loginPayload?.data?.accessType ??
    loginPayload?.data?.defaultAccType ??
    loginPayload?.data?.defaultAccessType;
  return value != null && String(value) !== '' ? String(value) : undefined;
}

function buildManagerContext(roleEntry, _index, apiContext) {
  const accountId = apiContext?.accountId || roleParentId(roleEntry);
  // Manager contexts must never carry personal Admin/Secondary/Generic roles.
  // Admin-as-manager UserRole entries still have role '1' but act as manager
  // in the selected admin account (ro/rw via accessType).
  let role = String(apiContext?.role || roleEntry?.role || MANAGER_ROLE);
  if (role === ADMIN_ROLE || role === SECONDARY_ADMIN_ROLE || role === GENERIC_ROLE) {
    role = MANAGER_ROLE;
  }
  return {
    id: contextId({ contextType: CONTEXT_TYPE_MANAGER, accountId }),
    contextType: CONTEXT_TYPE_MANAGER,
    accountId: String(accountId || ''),
    role,
    adminName: apiContext?.adminName || apiContext?.adminDisplayName || '',
    adminEmail: apiContext?.adminEmail || '',
    orgCount: apiContext?.orgCount,
    isDefault: apiContext?.isDefault === true || roleEntry?.isDefault === '1' || roleEntry?.isDefault === true,
    isVerified: apiContext?.isVerified !== false,
    accessType: apiContext?.accessType ?? roleEntry?.accessType,
  };
}

function personalRoleFromLoginData(loginData) {
  const roles = normalizeUserRoles(loginData?.UserRole || loginData?.data?.UserRole);
  const match =
    roles.find((r) => String(r?.role) === SECONDARY_ADMIN_ROLE) ||
    roles.find((r) => String(r?.role) === GENERIC_ROLE) ||
    roles.find((r) => String(r?.role) === ADMIN_ROLE);
  return match ? String(match.role) : ADMIN_ROLE;
}

function resolveMyInsightRole(loginData, apiContext) {
  const apiRole = apiContext?.role != null && String(apiContext.role) !== '' ? String(apiContext.role) : null;
  if (apiRole === GENERIC_ROLE || apiRole === SECONDARY_ADMIN_ROLE || apiRole === ADMIN_ROLE) {
    return apiRole;
  }
  return personalRoleFromLoginData(loginData);
}

function buildMyInsightContext(loginData, apiContext) {
  const accountId =
    apiContext?.accountId ||
    loginData?.accountId ||
    loginData?.data?.accountId ||
    loginData?.UserRole?.find((r) => String(r?.role) === ADMIN_ROLE)?.parentId ||
    loginData?.UserRole?.find((r) => String(r?.role) === SECONDARY_ADMIN_ROLE)?.parentId ||
    loginData?.UserRole?.find((r) => String(r?.role) === GENERIC_ROLE)?.parentId;
  const role = resolveMyInsightRole(loginData, apiContext);
  const resolvedAccountId = accountId ? String(accountId) : null;
  return {
    id: contextId({ contextType: CONTEXT_TYPE_MY_INSIGHT, accountId: resolvedAccountId }),
    contextType: CONTEXT_TYPE_MY_INSIGHT,
    accountId: resolvedAccountId,
    role,
    adminName: apiContext?.adminName || '',
    isDefault: apiContext?.isDefault === true,
    isVerified: true,
  };
}

/**
 * Build selectable account contexts from postUserLogin.
 * Only API-provided accountContexts are used so the picker stays behind the
 * MANAGER_MULTI_ADMIN_INVITE featureConf flag. Classic login (no accountContexts)
 * continues through CamSDK UserRole cookies.
 */
export function parseAccountContextsFromLoginResponse(loginResult) {
  const data = loginResult?.data || loginResult || {};
  const apiContexts = data.accountContexts || loginResult?.accountContexts;
  if (!Array.isArray(apiContexts) || apiContexts.length === 0) {
    return [];
  }
  return apiContexts
    .filter((c) => {
      if (c?.contextType === CONTEXT_TYPE_MY_INSIGHT || c?.contextType === 'MY_INSIGHT') {
        return Boolean(c?.accountId);
      }
      return c?.isVerified !== false;
    })
    .map((c, index) => {
      if (c.contextType === CONTEXT_TYPE_MY_INSIGHT || c.contextType === 'MY_INSIGHT') {
        return buildMyInsightContext(data, c);
      }
      return buildManagerContext({ parentId: c.accountId, role: c.role }, index, c);
    });
}

export function requiresAccountSelection(_contexts, loginResult) {
  if (loginResult?.data?.requiresAccountSelection === true) {
    return true;
  }
  if (loginResult?.requiresAccountSelection === true) {
    return true;
  }
  return false;
}

/**
 * Show the picker only on first login (or after the stored context was revoked).
 * Returning from Update Profile / app root must not force a re-select.
 */
export function shouldShowAccountPicker(contexts, loginResult) {
  const accessLevel = String(loginResult?.data?.accessLevel || loginResult?.accessLevel || '');
  // Support agents land on the customer impersonation list. Invited-admin
  // contexts stay available on Switch accounts.
  if (accessLevel === 's1' || accessLevel === 's2' || accessLevel === 's3') {
    return false;
  }
  if (!requiresAccountSelection(contexts, loginResult)) {
    return false;
  }
  return !getActiveContext(contexts);
}

export function getDefaultContext(contexts, apiDefault) {
  if (apiDefault?.contextType) {
    const match = contexts.find((c) => contextId(c) === contextId(apiDefault));
    if (match) return match;
  }
  return contexts.find((c) => c.isDefault) || contexts[0] || null;
}

/**
 * Resolve the account context the user is currently using (last accessed).
 */
export function getActiveContext(contexts, options = {}) {
  if (!Array.isArray(contexts) || contexts.length === 0) return null;

  const storedId = getActiveContextId();
  if (storedId) {
    const match = contexts.find((c) => contextId(c) === storedId);
    if (match) return match;
    if (storedId === CONTEXT_TYPE_MY_INSIGHT) {
      const legacy = contexts.find((c) => c.contextType === CONTEXT_TYPE_MY_INSIGHT);
      if (legacy) return legacy;
    }
  }

  const contextType = localStorage.getItem('insightActiveContextType');
  const storedAccountId = localStorage.getItem('insightActiveAccountId');
  if (contextType === CONTEXT_TYPE_MY_INSIGHT) {
    if (storedAccountId) {
      const match = contexts.find(
        (c) =>
          c.contextType === CONTEXT_TYPE_MY_INSIGHT && String(c.accountId) === String(storedAccountId)
      );
      if (match) return match;
    }
    const match = contexts.find((c) => c.contextType === CONTEXT_TYPE_MY_INSIGHT);
    if (match) return match;
  }
  if (contextType === CONTEXT_TYPE_MANAGER && storedAccountId) {
    const match = contexts.find(
      (c) => c.contextType === CONTEXT_TYPE_MANAGER && String(c.accountId) === String(storedAccountId)
    );
    if (match) return match;
  }

  const activeAccountId = options.activeAccountId;
  if (activeAccountId) {
    const managerMatch = contexts.find(
      (c) => c.contextType === CONTEXT_TYPE_MANAGER && String(c.accountId) === String(activeAccountId)
    );
    if (managerMatch) return managerMatch;
    const myInsightMatch = contexts.find(
      (c) =>
        c.contextType === CONTEXT_TYPE_MY_INSIGHT &&
        c.accountId != null &&
        String(c.accountId) === String(activeAccountId)
    );
    if (myInsightMatch) return myInsightMatch;
  }

  return null;
}

export function getInitialSelectedContext(contexts, options = {}) {
  const { switchMode = false, apiDefault = null, activeAccountId = null } = options;
  if (!Array.isArray(contexts) || contexts.length === 0) return null;
  if (switchMode) {
    const active = getActiveContext(contexts, { activeAccountId });
    if (active) return active;
    const myInsight = contexts.find((c) => c.contextType === CONTEXT_TYPE_MY_INSIGHT);
    if (myInsight) return myInsight;
  }
  return getDefaultContext(contexts, apiDefault);
}

/**
 * Parse accountContexts from login (`data`) or GET /user/v1/accountcontexts (`userInfo`).
 * Returns `{ ok: false }` when the payload is missing or the API reported failure.
 */
export function extractLiveAccountContexts(result) {
  if (!result || result.response?.status === false) {
    return { ok: false, contexts: [] };
  }
  const payload = result.userInfo || result.data || result.networkInfo || result;
  const contexts = parseAccountContextsFromLoginResponse({
    data: payload,
    accountContexts: payload?.accountContexts,
  });
  return { ok: true, contexts };
}

export function persistEnrichedLiveContexts(liveContexts) {
  const snapshot = loadLoginSnapshot() || loadPostLoginPending();
  const snapshotContexts = parseAccountContextsFromLoginResponse(snapshot);
  // Impersonation overlays the customer user. That live list must not replace
  // the support agent's invited-admin contexts (Switch accounts / picker).
  if (isSupportImpersonationSession() && snapshotContexts.length > 0) {
    persistAccountContexts(snapshotContexts);
    return snapshotContexts;
  }
  const contexts = enrichAccountContextsFromSnapshot(liveContexts, snapshot);
  if (isSupportAgentSession() && snapshotContexts.length > contexts.length) {
    persistAccountContexts(snapshotContexts);
    return snapshotContexts;
  }
  persistAccountContexts(contexts);
  return contexts;
}

export function isRevokedManagerSession(contexts) {
  if (!Array.isArray(contexts)) return false;
  const contextType = typeof localStorage !== 'undefined' ? localStorage.getItem('insightActiveContextType') : null;
  if (contextType !== CONTEXT_TYPE_MANAGER) return false;
  const accountId = typeof localStorage !== 'undefined' ? localStorage.getItem('insightActiveAccountId') : null;
  if (!accountId) return false;
  return !contexts.some(
    (c) => c.contextType === CONTEXT_TYPE_MANAGER && String(c.accountId) === String(accountId)
  );
}

/**
 * When live refresh changes the active manager's accessType (e.g. R/W → R/O),
 * update stamped session access + role cookie only. Does not navigate, clear org
 * caches, or re-run a full account switch.
 * @returns {boolean} true when the session was updated
 */
export function syncActiveManagerAccessFromLive(liveContexts, cookies) {
  if (!Array.isArray(liveContexts) || !cookies || typeof localStorage === 'undefined') {
    return false;
  }
  if (localStorage.getItem('insightActiveContextType') !== CONTEXT_TYPE_MANAGER) {
    return false;
  }
  const activeAccountId =
    localStorage.getItem('insightActiveAccountId') || cookies.get('accountId');
  if (!activeAccountId) {
    return false;
  }

  const active = liveContexts.find(
    (c) =>
      c.contextType === CONTEXT_TYPE_MANAGER && String(c.accountId) === String(activeAccountId)
  );
  if (!active || active.accessType == null || String(active.accessType) === '') {
    return false;
  }

  const nextAccess = String(active.accessType);
  const stored = localStorage.getItem('insightManagerAccessType');
  if (stored === nextAccess) {
    return false;
  }

  localStorage.setItem('insightManagerAccessType', nextAccess);

  const persisted = loadPersistedAccountContexts();
  if (persisted.length > 0) {
    const activeKey = contextId(active);
    persistAccountContexts(
      persisted.map((entry) =>
        contextId(entry) === activeKey ? { ...entry, accessType: nextAccess } : entry
      )
    );
  }

  syncManagerRoleFromSession(cookies);
  return true;
}

export function isSecondaryAdminSession() {
  const contexts = loadPersistedAccountContexts();
  const active = getActiveContext(contexts);
  if (active) {
    return String(active.role) === SECONDARY_ADMIN_ROLE;
  }
  if (contexts.some((context) => String(context?.role) === SECONDARY_ADMIN_ROLE)
      && !contexts.some((context) => context?.contextType === CONTEXT_TYPE_MANAGER)) {
    return true;
  }
  const snapshot = loadLoginSnapshot() || loadPostLoginPending();
  const data = snapshot?.data || snapshot || {};
  return normalizeUserRoles(data.UserRole).some((roleEntry) => String(roleEntry?.role) === SECONDARY_ADMIN_ROLE);
}

export function loadCachedAccountContexts(locationState) {
  let list = [];
  if (locationState?.contexts?.length) {
    persistAccountContexts(locationState.contexts);
    list = locationState.contexts;
  } else {
    list = loadPersistedAccountContexts();
  }
  const snapshot = loadLoginSnapshot() || loadPostLoginPending();
  return enrichAccountContextsFromSnapshot(list, snapshot);
}

/**
 * Build a selectable context from a UserRole entry when API accountContexts are absent.
 */
export function buildContextFromRoleEntry(roleEntry, loginPayload) {
  if (!roleEntry) return null;
  const roleStr = String(roleEntry.role);
  if (roleStr === GENERIC_ROLE || roleStr === ADMIN_ROLE || roleStr === SECONDARY_ADMIN_ROLE) {
    const personalAccountId =
      roleEntry.parentId || loginPayload?.accountId || loginPayload?.data?.accountId;
    if (
      roleStr === ADMIN_ROLE &&
      personalAccountId &&
      String(roleParentId(roleEntry)) !== String(personalAccountId)
    ) {
      // Prefer the role entry's accessType; login-level is only a fallback.
      // Force manager role — roleEntry.role is still '1' (admin) for this invite shape.
      return buildManagerContext(roleEntry, 0, {
        role: MANAGER_ROLE,
        accessType: roleEntry?.accessType ?? loginAccessType(loginPayload),
      });
    }
    return buildMyInsightContext(loginPayload?.data || loginPayload || {}, {
      accountId: personalAccountId,
      role: roleStr,
    });
  }
  return buildManagerContext(roleEntry, 0, {
    accessType: roleEntry?.accessType ?? loginAccessType(loginPayload),
  });
}

export function formatManagerAccessLabel(intl, accessType) {
  if (String(accessType) === '1') {
    return intl.formatMessage({ id: 'readOnly', defaultMessage: 'Read Only' });
  }
  if (String(accessType) === '2') {
    return intl.formatMessage({ id: 'readWrite', defaultMessage: 'Read/Write' });
  }
  return null;
}

/** Initials for account avatars (e.g. "Acme Networks MSP" → "AN"). */
export function getAccountInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/** Figma / theme blueGrey.800 for all account-picker avatars. */
export const ACCOUNT_AVATAR_MY_INSIGHT = '#37474F';

/**
 * Account avatar background. Figma uses blueGrey/800 for every
 * account row on Select Account / Switch Accounts.
 */
export function getAccountAvatarColor(_seedOrContext) {
  return ACCOUNT_AVATAR_MY_INSIGHT;
}

/** Display title for picker / header (My Insight stays localized). */
export function getAccountContextTitle(context, intl) {
  if (!context) return '';
  if (context.contextType === CONTEXT_TYPE_MY_INSIGHT) {
    if (
      String(context.role) === SECONDARY_ADMIN_ROLE &&
      context.adminName &&
      context.adminName !== 'My Insight Account'
    ) {
      return context.adminName;
    }
    return intl.formatMessage({ id: 'myInsightAccount', defaultMessage: 'My Insight Account' });
  }
  return (
    context.adminName ||
    context.adminEmail ||
    intl.formatMessage({ id: 'proAdminAccount', defaultMessage: 'Pro Admin Account' })
  );
}

/**
 * Role line for Figma account cards / Switch Accounts menu:
 * Admin | Manager (R/W) | Manager (Read Only)
 */
export function formatAccountContextRoleLabel(intl, context) {
  if (!context) return '';
  if (context.contextType === CONTEXT_TYPE_MY_INSIGHT) {
    if (String(context.role) === SECONDARY_ADMIN_ROLE) {
      return intl.formatMessage({ id: 'secondaryAdminRole', defaultMessage: 'Secondary Admin' });
    }
    return intl.formatMessage({ id: 'adminRole', defaultMessage: 'Admin' });
  }
  if (String(context.accessType) === '1') {
    return intl.formatMessage({ id: 'managerReadOnlyRole', defaultMessage: 'Manager (Read Only)' });
  }
  if (String(context.accessType) === '2') {
    return intl.formatMessage({ id: 'managerReadWriteRole', defaultMessage: 'Manager (R/W)' });
  }
  return intl.formatMessage({ id: 'managerBadge', defaultMessage: 'Manager' });
}

/**
 * Filter picker accounts by admin name, email, or the My Insight label.
 */
export function filterAccountContexts(contexts, searchText, labels = {}) {
  if (!Array.isArray(contexts) || contexts.length === 0) {
    return [];
  }
  const query = String(searchText || '').trim().toLowerCase();
  if (!query) {
    return contexts;
  }
  const myInsightTitle = labels.myInsightTitle || 'My Insight Account';
  return contexts.filter((context) => {
    if (!context) {
      return false;
    }
    const isMyInsight = context.contextType === CONTEXT_TYPE_MY_INSIGHT;
    const title = isMyInsight ? myInsightTitle : String(context.adminName || '');
    const adminName = String(context.adminName || '');
    const email = String(context.adminEmail || '');
    return (
      title.toLowerCase().includes(query) ||
      adminName.toLowerCase().includes(query) ||
      email.toLowerCase().includes(query)
    );
  });
}

/**
 * Fill missing manager accessType values from a stored login snapshot.
 */
export function enrichAccountContextsFromSnapshot(contexts, loginSnapshot) {
  if (!Array.isArray(contexts) || !contexts.length || !loginSnapshot) {
    return contexts;
  }
  const freshContexts = parseAccountContextsFromLoginResponse(loginSnapshot);
  if (!freshContexts.length) {
    return contexts;
  }
  return contexts.map((ctx) => {
    if (ctx.contextType !== CONTEXT_TYPE_MANAGER || ctx.accessType != null) {
      return ctx;
    }
    const freshCtx = freshContexts.find(
      (f) =>
        f.contextType === CONTEXT_TYPE_MANAGER &&
        (f.id === ctx.id || String(f.accountId) === String(ctx.accountId))
    );
    return freshCtx?.accessType != null ? { ...ctx, accessType: freshCtx.accessType } : ctx;
  });
}

export function persistAccountContexts(contexts) {
  if (!Array.isArray(contexts)) return;
  sessionStorage.setItem(ACCOUNT_CONTEXTS_STORAGE_KEY, JSON.stringify(contexts));
}

export function loadPersistedAccountContexts() {
  try {
    const raw = sessionStorage.getItem(ACCOUNT_CONTEXTS_STORAGE_KEY);
    const persisted = raw ? JSON.parse(raw) : [];
    const snapshotContexts = parseAccountContextsFromLoginResponse(
      loadLoginSnapshot() || loadPostLoginPending()
    );
    if (
      (isSupportImpersonationSession() || isSupportAgentSession()) &&
      snapshotContexts.length > (Array.isArray(persisted) ? persisted.length : 0)
    ) {
      return snapshotContexts;
    }
    return Array.isArray(persisted) ? persisted : [];
  } catch {
    return [];
  }
}

export function persistPostLoginPending(payload) {
  sessionStorage.setItem(POST_LOGIN_PENDING_STORAGE_KEY, JSON.stringify(payload));
  persistLoginSnapshot(payload);
}

export function persistLoginSnapshot(payload) {
  if (!payload) return;
  sessionStorage.setItem(LOGIN_SNAPSHOT_STORAGE_KEY, JSON.stringify(payload));
}

export function loadLoginSnapshot() {
  try {
    const raw = sessionStorage.getItem(LOGIN_SNAPSHOT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function loadPostLoginPending() {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_PENDING_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPostLoginPending() {
  sessionStorage.removeItem(POST_LOGIN_PENDING_STORAGE_KEY);
}

export function setActiveContextId(context) {
  sessionStorage.setItem(ACTIVE_CONTEXT_ID_STORAGE_KEY, contextId(context));
}

export function getActiveContextId() {
  return sessionStorage.getItem(ACTIVE_CONTEXT_ID_STORAGE_KEY);
}

export function clearActiveContextId() {
  sessionStorage.removeItem(ACTIVE_CONTEXT_ID_STORAGE_KEY);
}

export function clearContextCaches() {
  localStorage.removeItem('currentOrg');
  localStorage.removeItem('currentNetwork');
  localStorage.removeItem('userHadOrganizationsAtLogin');
}

export function resolveUserRoleKey(role, accessType) {
  const roleStr = String(role);
  if (roleStr === '1') return 'admin';
  // Missing accessType must not default to R/W — that unlocks write UI for RO managers.
  if (roleStr === '2') return String(accessType) === '2' ? 'rwManager' : 'roManager';
  if (roleStr === '3') return 'owner';
  if (roleStr === '4') return 'generic';
  if (roleStr === '5') return 'voucherAdmin';
  if (roleStr === '6') return 'preVoucherAdmin';
  if (roleStr === '7') return 'secondaryAdmin';
  if (roleStr === '8') return 'lineManager';
  return 'generic';
}

export function getUserRoleEntryForContext(userRoles, context) {
  if (!Array.isArray(userRoles) || !context) return userRoles?.[0] || null;
  if (context.contextType === CONTEXT_TYPE_MY_INSIGHT) {
    return (
      userRoles.find(
        (r) =>
          (String(r.role) === ADMIN_ROLE ||
            String(r.role) === GENERIC_ROLE ||
            String(r.role) === SECONDARY_ADMIN_ROLE) &&
          String(roleParentId(r)) === String(context.accountId)
      ) ||
      userRoles.find((r) => String(r.role) === SECONDARY_ADMIN_ROLE) ||
      userRoles.find((r) => String(r.role) === GENERIC_ROLE) ||
      userRoles.find((r) => String(r.role) === ADMIN_ROLE) ||
      (context.role
        ? { role: String(context.role), parentId: context.accountId }
        : { role: ADMIN_ROLE, parentId: context.accountId })
    );
  }
  // Manager / invite contexts: never fall back to UserRole[0] — that can stamp
  // another admin's parentId/role when the selected account has no matching entry.
  const matched =
    userRoles.find(
      (r) => String(r.role) === MANAGER_ROLE && String(roleParentId(r)) === String(context.accountId)
    ) || userRoles.find((r) => String(roleParentId(r)) === String(context.accountId));
  if (matched) {
    const matchedRole = String(matched.role);
    // Admin-as-manager parent match still has role '1' — keep parent/accessType, force manager.
    if (
      matchedRole === ADMIN_ROLE ||
      matchedRole === SECONDARY_ADMIN_ROLE ||
      matchedRole === GENERIC_ROLE
    ) {
      return { ...matched, role: MANAGER_ROLE };
    }
    return matched;
  }
  if (context.accountId == null || String(context.accountId) === '') {
    return null;
  }
  return {
    role: MANAGER_ROLE,
    parentId: context.accountId,
    ...(context.accessType != null && String(context.accessType) !== ''
      ? { accessType: context.accessType }
      : {}),
  };
}

/**
 * Role entry for the selected manager account only — never UserRole[0].
 * Using an unmatched first entry can copy another admin's R/W accessType.
 */
function managerRoleEntryForAccount(userRoles, accountId) {
  if (!Array.isArray(userRoles) || accountId == null || String(accountId) === '') {
    return null;
  }
  const accountKey = String(accountId);
  return (
    userRoles.find(
      (r) => String(r.role) === MANAGER_ROLE && String(roleParentId(r)) === accountKey
    ) || userRoles.find((r) => String(roleParentId(r)) === accountKey) || null
  );
}

/**
 * Resolve manager read-only vs read/write for a specific admin account.
 * Prefer per-context / matching role-entry accessType. Login-level accessType
 * is only used for an unambiguous classic single-manager login — never when
 * multiple manager accounts exist (that value is the first/default mapping).
 */
export function resolveManagerAccessTypeForContext(context, loginPayload) {
  if (context?.accessType != null && String(context.accessType) !== '') {
    return String(context.accessType);
  }

  const payloads = [loginPayload, loadLoginSnapshot(), loadPostLoginPending()].filter(Boolean);
  for (const payload of payloads) {
    const parsed = parseAccountContextsFromLoginResponse(payload);
    const match = parsed.find(
      (c) =>
        c.contextType === CONTEXT_TYPE_MANAGER &&
        String(c.accountId) === String(context?.accountId)
    );
    if (match?.accessType != null && String(match.accessType) !== '') {
      return String(match.accessType);
    }
  }

  for (const payload of payloads) {
    const roles = normalizeUserRoles(payload?.data?.UserRole || payload?.UserRole);
    const roleEntry = managerRoleEntryForAccount(roles, context?.accountId);
    if (roleEntry?.accessType != null && String(roleEntry.accessType) !== '') {
      return String(roleEntry.accessType);
    }
  }

  // Detect multi-admin across all payloads first so a thin switch payload
  // (no accountContexts) cannot stamp login-level R/W before the full snapshot.
  const anyMultiAdmin = payloads.some((payload) => {
    const managers = parseAccountContextsFromLoginResponse(payload).filter(
      (c) => c.contextType === CONTEXT_TYPE_MANAGER
    );
    return managers.length > 1;
  });
  if (anyMultiAdmin) {
    return undefined;
  }

  for (const payload of payloads) {
    const parsed = parseAccountContextsFromLoginResponse(payload);
    const managerContexts = parsed.filter((c) => c.contextType === CONTEXT_TYPE_MANAGER);
    if (managerContexts.length !== 1) {
      continue;
    }
    if (
      context?.accountId != null &&
      String(managerContexts[0].accountId) !== String(context.accountId)
    ) {
      continue;
    }
    const fallback = loginAccessType(payload);
    if (fallback != null) {
      return fallback;
    }
  }

  // Classic single-manager login: no accountContexts on any payload — login-level
  // accessType is unambiguous.
  const anyAccountContexts = payloads.some(
    (payload) => parseAccountContextsFromLoginResponse(payload).length > 0
  );
  if (!anyAccountContexts) {
    for (const payload of payloads) {
      const fallback = loginAccessType(payload);
      if (fallback != null) {
        return fallback;
      }
    }
  }

  return undefined;
}

/**
 * Apply selected context to cookies/localStorage used by API calls and role checks.
 */
export function supportAccessStForAccount(accountId, loginPayload) {
  const data = loginPayload?.data || {};
  const granted = data.grantedAccountIds;
  if (Array.isArray(granted)) {
    return granted.map(String).includes(String(accountId || '')) ? '1' : '0';
  }
  const contexts = loadPersistedAccountContexts();
  if (contexts.length > 1) {
    return '0';
  }
  return data.supportAccessSt === '1' || data.supportAccessSt === 1 ? '1' : '0';
}

export function updateGrantedAccountIdsInSnapshot(accountId, granted) {
  if (!accountId) return;
  const snapshot = loadLoginSnapshot() || { data: {} };
  const data = snapshot.data || {};
  const ids = new Set((data.grantedAccountIds || []).filter(Boolean).map(String));
  if (granted) {
    ids.add(String(accountId));
  } else {
    ids.delete(String(accountId));
  }
  data.grantedAccountIds = [...ids];
  data.supportAccessSt = ids.size > 0 ? '1' : '0';
  snapshot.data = data;
  persistLoginSnapshot(snapshot);
}

/**
 * Keep the login snapshot grant list in sync when a poll/reconcile reports
 * server supportAccessSt or grantedAccountIds. Otherwise the next account
 * switch can restore a revoked grant or drop a newly granted one.
 */
/**
 * Cookie/banner value for the current account after a poll or login payload.
 * Prefer grantedAccountIds so a grant on another account cannot light this one.
 * @returns {'1'|'0'|null} null when the payload has no support-access fields
 */
export function resolveLiveSupportAccessSt(userInfo, accountId) {
  if (!userInfo) return null;
  const incoming = userInfo.supportAccessSt ?? userInfo.suppAccessSt;
  const hasGrantList = Array.isArray(userInfo.grantedAccountIds);
  if ((incoming === undefined || incoming === null || incoming === '') && !hasGrantList) {
    return null;
  }
  reconcileSupportAccessSnapshot(userInfo, accountId);
  const snapshot = loadLoginSnapshot();
  return supportAccessStForAccount(accountId, {
    data: {
      grantedAccountIds: hasGrantList ? userInfo.grantedAccountIds : snapshot?.data?.grantedAccountIds,
      supportAccessSt: incoming ?? snapshot?.data?.supportAccessSt,
    },
  });
}

export function reconcileSupportAccessSnapshot(userInfo, currentAccountId) {
  if (!userInfo) return;
  const granted = userInfo.grantedAccountIds;
  if (Array.isArray(granted)) {
    const snapshot = loadLoginSnapshot() || { data: {} };
    const data = snapshot.data || {};
    data.grantedAccountIds = granted.filter(Boolean).map(String);
    data.supportAccessSt = data.grantedAccountIds.length > 0 ? '1' : '0';
    snapshot.data = data;
    persistLoginSnapshot(snapshot);
    return;
  }
  const incoming = userInfo.supportAccessSt ?? userInfo.suppAccessSt;
  if (incoming === undefined || incoming === null || incoming === '') return;
  if (currentAccountId) {
    updateGrantedAccountIdsInSnapshot(currentAccountId, String(incoming) === '1');
    return;
  }
  const snapshot = loadLoginSnapshot();
  if (!snapshot?.data) return;
  snapshot.data.supportAccessSt = String(incoming) === '1' ? '1' : '0';
  persistLoginSnapshot(snapshot);
}

export function applyAccountContext(context, loginPayload, cookies) {
  const data = loginPayload?.data || {};
  const email = data.email || '';
  const encKey = email.split('@')[0]?.toLowerCase() || '';
  const secure = cookieSecure();
  const userRoles = normalizeUserRoles(data.UserRole);

  let role = String(context?.role || ADMIN_ROLE);
  let roleEntry = null;
  let accessType;

  if (context.contextType === CONTEXT_TYPE_MY_INSIGHT) {
    roleEntry = getUserRoleEntryForContext(userRoles, context);
    role = String(roleEntry?.role || context.role || ADMIN_ROLE);
    accessType = undefined;
  } else {
    roleEntry = getUserRoleEntryForContext(userRoles, context);
    role = String(roleEntry?.role ?? context.role ?? MANAGER_ROLE);
    // Belt-and-suspenders: manager contexts must never stamp admin cookies.
    if (role === ADMIN_ROLE || role === SECONDARY_ADMIN_ROLE || role === GENERIC_ROLE) {
      role = MANAGER_ROLE;
    }
    accessType = resolveManagerAccessTypeForContext(context, loginPayload);
  }

  const userType = resolveUserRoleKey(role, accessType);

  let accountId = '';
  let userAccountId = data._id || '';

  if (context.contextType === CONTEXT_TYPE_MY_INSIGHT) {
    const personalAccountId = String(
      context.accountId || loginPayload?.accountId || data.accountId || roleEntry?.parentId || ''
    );
    if (!personalAccountId) {
      return;
    }
    accountId = personalAccountId;
    userAccountId = personalAccountId;
  } else if (String(role) === '7') {
    accountId = roleParentId(roleEntry) || context.accountId;
    userAccountId = accountId;
  } else if (['2', '3', '5', '6', '8'].includes(String(role))) {
    accountId = roleParentId(roleEntry) || context.accountId;
    userAccountId = data._id || userAccountId;
  } else {
    accountId = loginPayload?.accountId || data.accountId || context.accountId || '';
    userAccountId = accountId;
  }

  cookies.set('userRole', CryptoJS.AES.encrypt(userType, encKey).toString(), { secure, path: '/' });
  cookies.set('accountId', accountId, { secure, path: '/' });
  cookies.set('userAccountId', userAccountId, { secure, path: '/' });
  cookies.set('_Id', data._id, { secure, path: '/' });
  cookies.set('email', email, { secure, path: '/' });
  cookies.set('userid', data.userId, { secure, path: '/' });
  cookies.set('user_email', email, { secure, path: '/' });
  cookies.set('userId', data.userId ?? data.preferredUsername, { secure, path: '/' });
  const scopedSupportAccessSt = supportAccessStForAccount(accountId, loginPayload);
  cookies.set('supportAccessSt', scopedSupportAccessSt, { secure, path: '/' });
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(
      new CustomEvent(scopedSupportAccessSt === '1' ? 'supportAccessGranted' : 'supportAccessRevoked')
    );
  }

  const firstName = sanitizePersonName(data.firstName);
  const lastName = sanitizePersonName(data.lastName);
  if (firstName) {
    cookies.set('firstName', firstName, { secure, path: '/' });
  } else {
    cookies.remove('firstName', { path: '/' });
  }
  if (lastName) {
    cookies.set('lastName', lastName, { secure, path: '/' });
  } else {
    cookies.remove('lastName', { path: '/' });
  }

  localStorage.setItem('email', email);
  localStorage.setItem('UserId', data.userId);
  localStorage.setItem('insightActiveContextType', context.contextType);
  if (context.accountId) {
    localStorage.setItem('insightActiveAccountId', context.accountId);
  } else {
    localStorage.removeItem('insightActiveAccountId');
  }
  if (context.contextType === CONTEXT_TYPE_MANAGER && accessType != null) {
    localStorage.setItem('insightManagerAccessType', String(accessType));
  } else {
    localStorage.removeItem('insightManagerAccessType');
  }

  // Stale support sub-role cookies block all checkUser() calls, but an ACTIVE support
  // impersonation needs userSubRole=supportAgent to keep its denials. syncManagerRoleFromSession
  // (called below) clears it only when this is not a support session; do not clear it here too.

  setActiveContextId(context);

  const persistedContexts = loadPersistedAccountContexts();
  if (persistedContexts.length > 0) {
    const activeId = contextId(context);
    persistAccountContexts(
      persistedContexts.map((entry) =>
        contextId(entry) === activeId && entry.contextType === CONTEXT_TYPE_MANAGER
          ? { ...entry, accessType: accessType ?? entry.accessType }
          : entry
      )
    );
  }

  syncManagerRoleFromSession(cookies);
  clearContextCaches();
}

/**
 * Align manager role cookie with active account access type and drop stale support sub-roles.
 */
export function syncManagerRoleFromSession(cookies) {
  const accessLevel = typeof localStorage !== 'undefined' ? localStorage.getItem('accessLevel') : null;
  const isSupportSession = accessLevel === 's1' || accessLevel === 's2' || accessLevel === 's3';

  if (!isSupportSession) {
    cookies.remove('userSubRole', { path: '/' });
  }

  const contextType = localStorage.getItem('insightActiveContextType');
  const accessType = localStorage.getItem('insightManagerAccessType');
  if (contextType !== CONTEXT_TYPE_MANAGER || accessType == null || accessType === '') {
    return;
  }

  const email = localStorage.getItem('email') || cookies.get('email') || cookies.get('user_email');
  if (!email) return;

  try {
    const encKey = email.split('@')[0].toLowerCase();
    const userRoleCookie = cookies.get('userRole');
    if (!userRoleCookie) return;

    const currentRole = CryptoJS.AES.decrypt(userRoleCookie, encKey).toString(CryptoJS.enc.Utf8);
    const expectedRole = String(accessType) === '2' ? 'rwManager' : 'roManager';
    if (currentRole !== expectedRole) {
      cookies.set('userRole', CryptoJS.AES.encrypt(expectedRole, encKey).toString(), {
        secure: cookieSecure(),
        path: '/',
      });
    }
  } catch {
    // ignore decrypt errors
  }
}

export function hasMultipleAccountContexts() {
  if (loadPersistedAccountContexts().length > 1) {
    return true;
  }
  return parseAccountContextsFromLoginResponse(loadLoginSnapshot() || loadPostLoginPending()).length > 1;
}

/**
 * Manager contexts tied to distinct admin accounts.
 * Backend only returns accountContexts when MANAGER_MULTI_ADMIN_INVITE is on.
 */
export function getManagerAccountContexts(contexts = loadPersistedAccountContexts()) {
  if (!Array.isArray(contexts)) return [];
  return contexts.filter((context) => context?.contextType === CONTEXT_TYPE_MANAGER && context?.accountId);
}

/**
 * True when this manager is linked to more than one admin.
 */
export function hasMultipleManagerAdmins(contexts = loadPersistedAccountContexts()) {
  const adminAccountIds = new Set(
    getManagerAccountContexts(contexts).map((context) => String(context.accountId))
  );
  return adminAccountIds.size > 1;
}

/**
 * Classic cannot scope a session across multiple accounts, so hide Switch to
 * Classic for any multi-account user: manager of 2+ admins, secondary of 2+
 * primaries, or a primary who is also a manager/secondary elsewhere.
 */
export function shouldBlockSwitchToClassic(contexts = loadPersistedAccountContexts()) {
  if (!Array.isArray(contexts)) {
    return false;
  }
  const accountIds = new Set(
    contexts
      .filter((context) => context?.accountId != null && String(context.accountId) !== '')
      .map((context) => String(context.accountId))
  );
  return accountIds.size > 1;
}

/** Clear all multi-admin account context state on logout. */
export function clearAllAccountContextState() {
  try {
    sessionStorage.removeItem(ACCOUNT_CONTEXTS_STORAGE_KEY);
    sessionStorage.removeItem(ACTIVE_CONTEXT_ID_STORAGE_KEY);
    sessionStorage.removeItem(POST_LOGIN_PENDING_STORAGE_KEY);
    sessionStorage.removeItem(LOGIN_SNAPSHOT_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_CONTEXT_ID_STORAGE_KEY);
    localStorage.removeItem('insightActiveContextType');
    localStorage.removeItem('insightActiveAccountId');
    localStorage.removeItem('insightManagerAccessType');
  } catch {
    // ignore storage errors during logout
  }
}

export function sanitizePersonName(value) {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text || text === 'undefined' || text === 'null') return '';
  return text;
}

/**
 * Resolve a friendly dashboard welcome name from login snapshot and cookies.
 */
export function getWelcomeDisplayName(getCookie) {
  const readCookie = typeof getCookie === 'function' ? getCookie : () => undefined;
  const snapshot = loadLoginSnapshot() || loadPostLoginPending();
  const candidates = [snapshot?.data?.firstName, readCookie('firstName')];
  for (const candidate of candidates) {
    const name = sanitizePersonName(candidate);
    if (name) return name;
  }
  const email =
    readCookie('email') ||
    readCookie('user_email') ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('email') : null);
  if (email && String(email).includes('@')) {
    return String(email).split('@')[0];
  }
  return 'User';
}
